import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import type { ResponsiveStylesStructured } from '@duda-co/responsive-styles/types/ResponsiveStylesTypes';
import { localizationService } from '@shared-services/localization-service';
import { RuntimeWidget } from 'client/types';
import { Div, Label } from 'client/widget-components/basicComponents';
import FileUpload from 'client/widget-components/components/FileUpload';
import type {
    FileUploadState,
    UploadResult,
} from 'client/widget-components/components/FileUpload/types';
import { mergeResponsiveStyles } from 'client/widget-components/responsive-styles/responsiveStylesService';
import type { PickerOptions } from 'client/widget-components/services/filestack';
import React, { useCallback, useMemo, useState } from 'react';
import { InvisibleInputWithEvent } from './InvisibleInputWithEvent';
import { RuntimeFormFileInputProps } from './types';

export const RuntimeFormFileInput: RuntimeWidget<RuntimeFormFileInputProps> = ({
    label,
    required = false,
    buttonText,
    showLabel = true,
    apiKey,
    storeRegion,
    storeContainer,
    storePath,
    _styles,
    widgetId,
}: RuntimeFormFileInputProps) => {
    const [uploadedFiles, setUploadedFiles] = useState<UploadResult[]>([]);

    const handleFileUploadChange = useCallback((state: FileUploadState) => {
        setUploadedFiles(state.files);
    }, []);

    // This is hidden by Form CSS until submission event
    const errorMessage = useMemo(() => {
        if (!required || uploadedFiles.length) {
            return;
        }
        return (
            <FileUpload.Error
                // Important, used for form validation process
                className='error-message'
                data-error='required'
                aria-live='polite'
            >
                {localizationService.str(
                    'rt.advancedFormInput.requiredMessage'
                )}
            </FileUpload.Error>
        );
    }, [uploadedFiles.length, required]);

    const pickerOptions: Partial<PickerOptions> = {
        storeTo: {
            location: 'S3',
            access: 'public',
            region: storeRegion,
            container: storeContainer,
            path: storePath,
        },
        maxFiles: 100, // Just to make sure not overridden in FileUpload
    };

    const wrapperStyles = mergeResponsiveStyles([
        wrapperDefaultStyles,
        _styles?.wrapper,
    ]);

    const fileUploadStyles = {
        button: {
            ..._styles?.uploadButton,
            root: mergeResponsiveStyles([
                buttonDefaultStyles,
                _styles?.uploadButton?.root,
            ]),
            rootHover: _styles?.uploadButton?.rootHover,
            rootDisabled: _styles?.uploadButton?.rootDisabled,
            text: mergeResponsiveStyles([
                textDefaultStyles,
                _styles?.uploadButton?.text,
            ]),
            rootHoverText: _styles?.uploadButton?.rootHoverText,
            disabledText: _styles?.uploadButton?.disabledText,
            icon: mergeResponsiveStyles([
                iconDefaultStyles,
                _styles?.uploadButton?.icon,
            ]),
            rootHoverIcon: _styles?.uploadButton?.rootHoverIcon,
            iconDisabled: _styles?.uploadButton?.iconDisabled,
        },
    };

    return (
        <Div
            styles={wrapperStyles}
            data-auto={`file-input-${widgetId}`}
            data-grab='file-input-wrapper'
            className='advanced-form-field' // Important, used for form validation process
        >
            <Label
                hidden={!showLabel}
                data-grab='file-input-label'
                className='advanced-form-label' // Important, used for form validation process
                styles={_styles?.label}
                htmlFor={`file-input-${widgetId}`}
            >
                {label}
            </Label>
            <FileUpload
                buttonText={buttonText}
                onChange={handleFileUploadChange}
                apiKey={apiKey}
                error={errorMessage}
                pickerOptions={pickerOptions}
                styles={fileUploadStyles}
                dataGrab='file-input-upload'
                dataAuto={`file-input-upload-${widgetId}`}
                // Note: buttonClassName is used to inherit some global button styles from the theme.
                buttonClassName='button_2'
            />
            {!uploadedFiles?.length && (
                <InvisibleInputWithEvent
                    type='file'
                    required={required}
                    name={widgetId}
                    className='advanced-form-input' // Important, used for form validation process
                />
            )}
            {uploadedFiles.map((file, index) => (
                <InvisibleInputWithEvent
                    key={file.key || file.handle || file.url}
                    type='hidden'
                    name={widgetId}
                    required={required} // Important, needed for asterisk indication
                    className='advanced-form-input' // Important, used for form validation process
                    value={encodeURIComponent(file.key || '')}
                    data-auto={`file-input-hidden-${index}`}
                />
            ))}
        </Div>
    );
};

const wrapperDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
};

const buttonDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        width: '150px',
        height: '40px',
        borderRadius: '4px',
        borderWidth: '1px',
        paddingTop: '8px',
        padding: '8px 18px',
        backgroundColor: 'var(--btn-bg-color)',
        borderColor: 'var(--color_1);',
        margin: '0px',
        direction: 'ltr',
        backgroundImage: 'none',
        boxShadow: 'none',
        textAlign: 'center',
    },
};

const textDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        color: 'var(--btn-text-color)',
        padding: '0px',
        fontSize: '14px',
        fontWeight: 'normal',
        textDecoration: 'none',
        fontStyle: 'normal',
    },
};

const iconDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        color: 'var(--btn-icon-color)',
        display: 'flex',
        width: '16px',
        height: '16px',
        marginInlineEnd: '8px',
    },
};

export default RuntimeFormFileInput;
